import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Shield, Clock, CreditCard } from 'lucide-react';
const PLANS = {
  plus: {
    id: 'plus',
    title: 'Guardia Clínica PLUS',
    subtitle: '8 tomos + 6 bonos + Tomos 7 y 8',
    original: '$47.990',
    price: '$29.990',
    url: 'https://checkout.mundodigitalarg.com/cart/48230777946353:1',
    features: ['Todo lo del Atlas Completo', 'Tomo 7: Urgencias Respiratorias', 'Tomo 8: Toxicología y Sobredosis']
  },
  base: {
    id: 'base',
    title: 'Guardia Clínica',
    subtitle: '6 tomos + 6 bonos clínicos',
    original: '$34.990',
    price: '$19.990',
    url: 'https://checkout.mundodigitalarg.com/cart/48230777094385:1',
    features: ['6 Tomos Visuales Completos', '6 Bonos Clínicos Gratis']
  }
};
interface PricingSelectorProps {
  compact?: boolean;
}
export function PricingSelector({
  compact = false
}: PricingSelectorProps) {
  const [selected, setSelected] = useState<'plus' | 'base'>('plus');
  const selectedPlan = PLANS[selected];
  return <div className="w-full" data-id="element-256">
      {/* Plans stacked as ROWS */}
      <div className="flex flex-col gap-2.5 mb-4" data-id="element-257">
        {/* PLUS Plan (Highlighted, first) */}
        <button type="button" onClick={() => setSelected('plus')} className={`relative text-left rounded-xl transition-all border-2 overflow-hidden ${selected === 'plus' ? 'border-urgency-red bg-red-50 shadow-lg' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-258">
          {/* MÁS ELEGIDA ribbon */}
          <div className="absolute top-0 right-0 bg-urgency-red text-white font-black px-2.5 py-1 rounded-bl-lg text-[9px] md:text-[10px] tracking-wider shadow-sm z-10" data-id="element-259">
            ⭐ MÁS ELEGIDA
          </div>

          <div className="flex items-center gap-3 p-3 md:p-4 pr-4" data-id="element-260">
            {/* Radio */}
            <div className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selected === 'plus' ? 'border-urgency-red bg-urgency-red' : 'border-gray-300 bg-white'}`} data-id="element-261">
              {selected === 'plus' && <Check size={14} className="text-white" strokeWidth={3} data-id="element-262" />}
            </div>

            {/* Title + subtitle */}
            <div className="flex-1 min-w-0 pr-2" data-id="element-263">
              <h3 className={`font-heading font-extrabold leading-tight text-sm md:text-base ${selected === 'plus' ? 'text-medical-blue' : 'text-gray-900'}`} data-id="element-264">
                Atlas PLUS
              </h3>
              <p className="text-[10px] md:text-xs text-gray-500 leading-tight mt-0.5" data-id="element-265">
                8 tomos + 6 bonos
              </p>
            </div>

            {/* Price */}
            <div className="shrink-0 text-right" data-id="element-266">
              <div className="text-gray-400 line-through text-[10px] md:text-xs leading-none" data-id="element-267">
                {PLANS.plus.original}
              </div>
              <div className={`font-black leading-tight ${compact ? 'text-lg md:text-xl' : 'text-xl md:text-2xl'} ${selected === 'plus' ? 'text-urgency-red' : 'text-gray-900'}`} data-id="element-268">
                {PLANS.plus.price}
              </div>
            </div>
          </div>

          {/* Features (only when not compact AND selected) */}
          {!compact && selected === 'plus' && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} className="overflow-hidden border-t border-red-200/60" data-id="element-269">
              <div className="px-4 py-2.5 space-y-1 bg-red-50/50" data-id="element-270">
                {PLANS.plus.features.map((f, i) => <div key={i} className="flex items-start gap-2" data-id="element-271">
                    <Check size={12} className="text-success-green shrink-0 mt-0.5" strokeWidth={3} data-id="element-272" />
                    <span className="text-[11px] md:text-xs text-gray-700 leading-tight" data-id="element-273">
                      {f}
                    </span>
                  </div>)}
              </div>
            </motion.div>}
        </button>

        {/* BASE Plan */}
        <button type="button" onClick={() => setSelected('base')} className={`relative text-left rounded-xl transition-all border-2 overflow-hidden ${selected === 'base' ? 'border-medical-blue bg-blue-50 shadow-lg' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-274">
          <div className="flex items-center gap-3 p-3 md:p-4" data-id="element-275">
            {/* Radio */}
            <div className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selected === 'base' ? 'border-medical-blue bg-medical-blue' : 'border-gray-300 bg-white'}`} data-id="element-276">
              {selected === 'base' && <Check size={14} className="text-white" strokeWidth={3} data-id="element-277" />}
            </div>

            {/* Title + subtitle */}
            <div className="flex-1 min-w-0 pr-2" data-id="element-278">
              <h3 className={`font-heading font-extrabold leading-tight text-sm md:text-base ${selected === 'base' ? 'text-medical-blue' : 'text-gray-900'}`} data-id="element-279">
                Atlas Clínica
              </h3>
              <p className="text-[10px] md:text-xs text-gray-500 leading-tight mt-0.5" data-id="element-280">
                6 tomos + 6 bonos
              </p>
            </div>

            {/* Price */}
            <div className="shrink-0 text-right" data-id="element-281">
              <div className="text-gray-400 line-through text-[10px] md:text-xs leading-none" data-id="element-282">
                {PLANS.base.original}
              </div>
              <div className={`font-black leading-tight ${compact ? 'text-lg md:text-xl' : 'text-xl md:text-2xl'} ${selected === 'base' ? 'text-medical-blue' : 'text-gray-900'}`} data-id="element-283">
                {PLANS.base.price}
              </div>
            </div>
          </div>

          {!compact && selected === 'base' && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} className="overflow-hidden border-t border-blue-200/60" data-id="element-284">
              <div className="px-4 py-2.5 space-y-1 bg-blue-50/50" data-id="element-285">
                {PLANS.base.features.map((f, i) => <div key={i} className="flex items-start gap-2" data-id="element-286">
                    <Check size={12} className="text-success-green shrink-0 mt-0.5" strokeWidth={3} data-id="element-287" />
                    <span className="text-[11px] md:text-xs text-gray-700 leading-tight" data-id="element-288">
                      {f}
                    </span>
                  </div>)}
              </div>
            </motion.div>}
        </button>
      </div>

      {/* Shared dynamic CTA */}
      <motion.a href={selectedPlan.url} target="_blank" rel="noopener noreferrer" whileHover={{
      scale: 1.01
    }} whileTap={{
      scale: 0.98
    }} className="w-full bg-urgency-red text-white font-bold text-base md:text-lg py-4 px-4 rounded-xl shadow-[0_4px_20px_rgba(215,38,56,0.4)] hover:bg-red-700 transition-colors animate-pulse-subtle flex items-center justify-center gap-2 text-center" data-id="element-289">
        <span data-id="element-290">
          💥 QUIERO {selected === 'plus' ? 'EL ATLAS PLUS' : 'EL ATLAS'} ·{' '}
          {selectedPlan.price}
        </span>
      </motion.a>

      {/* Trust strip */}
      <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2" data-id="element-291">
        <div className="flex items-center gap-1.5 text-gray-500 text-[10px] md:text-xs font-medium" data-id="element-292">
          <Shield size={12} data-id="element-293" /> Pago seguro
        </div>
        <div className="flex items-center gap-1.5 text-gray-500 text-[10px] md:text-xs font-medium" data-id="element-294">
          <Clock size={12} data-id="element-295" /> Acceso en 5 min
        </div>
        <div className="flex items-center gap-1.5 text-gray-500 text-[10px] md:text-xs font-medium" data-id="element-296">
          <Shield size={12} className="text-success-green" data-id="element-297" /> Garantía 7 días
        </div>
        <div className="flex items-center gap-1.5 text-gray-500 text-[10px] md:text-xs font-medium" data-id="element-298">
          <CreditCard size={12} className="text-blue-500" data-id="element-299" /> Mercado Pago
        </div>
      </div>
    </div>;
}